import React from "react";
import { useFetch } from "@/hooks/useApi";
import { Loader, StatusPill, Plate } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { gbp, num, ukDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";

const Metric = ({ label, value, accent = "text-foreground" }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono font-semibold ${accent}`}>{value}</span>
  </div>
);

export default function DriverDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading } = useFetch("/dashboard/driver");
  if (loading || !data) return <Loader label="Loading your dashboard" />;

  const { month, week, tickets } = data;
  const deductTickets = tickets.filter((t) => t.deduct_from_earnings);

  return (
    <div className="fp-fade-in max-w-2xl mx-auto" data-testid="driver-dashboard">
      <div className="mb-6">
        <p className="text-sm text-muted-foreground">Welcome back,</p>
        <h1 className="font-display text-2xl font-bold">{user?.name}</h1>
      </div>

      <Button
        className="w-full h-14 text-base mb-6"
        onClick={() => navigate("/my-stops")}
        data-testid="submit-stops-cta"
      >
        <Icons.MapPin className="h-5 w-5 mr-2" /> Submit Today's Stops
      </Button>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <Card className="p-5 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Today Submitted</p>
          <p className="font-mono text-3xl font-bold mt-2">{num(data.today_submitted)}</p>
        </Card>
        <Card className="p-5 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Today Approved</p>
          <p className="font-mono text-3xl font-bold mt-2 text-emerald-400">
            {data.today_approved ? num(data.today_approved) : "—"}
          </p>
        </Card>
      </div>

      <Card className="p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold">Assigned Van</h3>
          <Icons.Truck className="h-4 w-4 text-muted-foreground" />
        </div>
        {data.assigned_van ? (
          <div className="flex items-center justify-between">
            <div>
              <Plate reg={data.assigned_van.registration} />
              <p className="text-sm text-muted-foreground mt-2">{data.assigned_van.make_model}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Rate multiplier</p>
              <p className="font-mono font-bold text-lg text-primary">×{data.rate_multiplier}</p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No van assigned yet</p>
        )}
      </Card>

      <Card className="p-5 mb-4">
        <h3 className="font-display font-semibold mb-2">This Month's Earnings</h3>
        <Metric label={`Approved stops`} value={num(month.approved_stops)} />
        <Metric label={`Base rate × multiplier`} value={`${gbp(month.base_rate)} × ${month.rate_multiplier}`} />
        <Metric label="Gross earnings" value={gbp(month.gross)} accent="text-emerald-400" />
        {month.min_guarantee > 0 && (
          <Metric
            label={`Min guarantee (${gbp(month.min_guarantee)}/day)`}
            value={month.guarantee_topup > 0 ? `+ ${gbp(month.guarantee_topup)} applied` : "target met"}
            accent="text-blue-400"
          />
        )}
        <Metric label={`Ticket deductions (${month.ticket_count})`} value={`- ${gbp(month.deductions)}`} accent="text-rose-400" />
        <div className="flex items-center justify-between pt-3 mt-1">
          <span className="font-semibold">Final income</span>
          <span className="font-mono text-2xl font-bold text-primary">{gbp(month.net)}</span>
        </div>
      </Card>

      <Card className="p-5 mb-4">
        <h3 className="font-display font-semibold mb-2">This Week</h3>
        <Metric label="Approved stops" value={num(week.approved_stops)} />
        <Metric label="Net income" value={gbp(week.net)} accent="text-primary" />
        <Metric label="Estimated today (pending)" value={gbp(data.estimated_today)} accent="text-amber-400" />
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold">Tickets & Deductions</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate("/my-tickets")}>View all</Button>
        </div>
        {tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tickets — great driving!</p>
        ) : (
          <div className="space-y-2">
            {tickets.slice(0, 4).map((t) => (
              <div key={t.id} className="flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium">{t.type}</p>
                  <p className="text-xs text-muted-foreground">{ukDate(t.date)} · {t.reference}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-semibold text-rose-400">{gbp(t.amount)}</p>
                  {t.deduct_from_earnings && <span className="text-[10px] text-rose-400">deducted</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {deductTickets.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            {gbp(month.deductions)} deducted from this month's earnings.
          </p>
        )}
      </Card>
    </div>
  );
}
